import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { StatusBadge, Surface, SurfaceHeader } from "../components/PortalPrimitives";
import { fetchHome } from "../lib/api";
import type { HomePortfolioDocumentFeed, InboxItem } from "../lib/types";

const documentDateFormatter = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});

function timeAgo(iso: string): string {
  const seconds = Math.max(0, (Date.now() - Date.parse(iso)) / 1000);
  if (seconds < 3600) return `${Math.max(1, Math.round(seconds / 60))}m ago`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h ago`;
  return `${Math.round(seconds / 86400)}d ago`;
}

function documentDate(value: string | null): string {
  if (!value) return "Date unavailable";
  const parsed = new Date(value.length === 10 ? `${value}T00:00:00Z` : value);
  return Number.isNaN(parsed.getTime()) ? value : documentDateFormatter.format(parsed);
}

function documentType(value: string | null): string {
  if (!value) return "Type unavailable";
  const normalized = value.replace(/[_-]+/g, " ").trim();
  return normalized ? `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}` : "Type unavailable";
}

function Item({ item }: { item: InboxItem }) {
  const body = (
    <div className="home-item-body">
      <div className="home-item-title">
        <span>{item.title}</span>
        {item.chip ? <StatusBadge tone={item.chip.tone}>{item.chip.label}</StatusBadge> : null}
      </div>
      <div className="home-item-detail">{item.detail}</div>
    </div>
  );
  // Portal paths route inside the SPA; anything else (e.g. legacy
  // /dashboard/* links from the interim inbox payload) is a plain anchor.
  const link = item.deep_link;
  return (
    <li className="home-item">
      <time className="home-item-time" dateTime={item.when}>
        {timeAgo(item.when)}
      </time>
      {link && link.startsWith("/app") ? (
        <Link className="home-item-link" to={link.replace(/^\/app/, "") || "/"}>
          {body}
        </Link>
      ) : link ? (
        <a className="home-item-link" href={link}>
          {body}
        </a>
      ) : (
        body
      )}
    </li>
  );
}

function Section({
  title,
  detail,
  items,
  className = "",
}: {
  title: string;
  detail: string;
  items: InboxItem[];
  className?: string;
}) {
  const titleId = `home-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`;
  return (
    <Surface className={`home-surface ${className}`.trim()} aria-labelledby={titleId}>
      <SurfaceHeader title={title} titleId={titleId} detail={detail} count={items.length} />
      {items.length === 0 ? (
        <div className="home-empty">Nothing here — quiet is good.</div>
      ) : (
        <ul className="home-list">
          {items.map((item) => (
            <Item key={item.id} item={item} />
          ))}
        </ul>
      )}
    </Surface>
  );
}

function LatestPortfolioDocuments({ feed }: { feed?: HomePortfolioDocumentFeed }) {
  if (!feed) return null;

  const documents = feed.documents;
  return (
    <Surface className="home-documents" aria-labelledby="home-latest-portfolio-documents">
      <SurfaceHeader
        title="Latest portfolio documents"
        titleId="home-latest-portfolio-documents"
        detail="Newest documents added for portfolio-tier companies."
        count={feed.status === "ok" ? documents.length : undefined}
        action={
          <Link className="home-surface-action" to="/documents">
            View all documents
          </Link>
        }
      />
      {feed.status === "unavailable" ? (
        <div className="home-document-state" role="status">
          Portfolio documents are temporarily unavailable. Your attention items are still current.
        </div>
      ) : documents.length === 0 ? (
        <div className="home-document-state">No portfolio documents have arrived yet.</div>
      ) : (
        <>
          <div className="home-document-columns" aria-hidden="true">
            <span>Added</span>
            <span>Company</span>
            <span>Document</span>
            <span>Source / type</span>
            <span>Document date</span>
          </div>
          <ul className="home-document-list">
            {documents.map((document) => {
              const target = document.deep_link.replace(/^\/app/, "") || "/";
              return (
                <li key={document.document_id}>
                  <Link className="home-document-row" to={target}>
                    <time className="home-document-added" dateTime={document.ingested_at ?? undefined}>
                      {document.ingested_at ? timeAgo(document.ingested_at) : "Time unavailable"}
                    </time>
                    <span className="home-document-company">
                      <strong>{document.ticker || "—"}</strong>
                      <span>{document.company_name || "Company unavailable"}</span>
                    </span>
                    <span className="home-document-copy">
                      <strong>{document.title || "Untitled document"}</strong>
                    </span>
                    <span className="home-document-source">
                      <span>{document.source_name || "Source unavailable"}</span>
                      <span>{documentType(document.document_type)}</span>
                    </span>
                    <time className="home-document-date" dateTime={document.document_date ?? undefined}>
                      {documentDate(document.document_date)}
                    </time>
                  </Link>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </Surface>
  );
}

export default function Home() {
  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ["home"],
    queryFn: fetchHome,
  });

  if (isLoading) {
    return (
      <div className="home-state" role="status" aria-live="polite">
        <span className="home-state-pulse" aria-hidden="true" />
        <div>
          <strong>Loading your operating view</strong>
          <span>Checking attention items and active work…</span>
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="home-state home-state--error" role="alert">
        <div>
          <strong>Home could not be loaded</strong>
          <span>Your session is still active. Retry the inbox request.</span>
        </div>
        <button className="btn" type="button" onClick={() => void refetch()}>
          Try again
        </button>
      </div>
    );
  }

  const attentionCount = data.attention.length;

  return (
    <div className="home-page">
      <header className="home-pagehead">
        <div className="home-heading">
          <div className="home-eyebrow">Daily operating view</div>
          <h1>Needs attention</h1>
          <p>{data.headline}</p>
        </div>
        <div className="home-page-actions">
          <StatusBadge tone={attentionCount > 0 ? "red" : "green"}>
            {attentionCount} {attentionCount === 1 ? "attention item" : "attention items"}
          </StatusBadge>
          <button
            className="btn home-refresh"
            type="button"
            disabled={isFetching}
            onClick={() => void refetch()}
          >
            {isFetching ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </header>

      <div className="home-layout">
        <Section
          title="Attention"
          detail="Signals and changes that need review."
          items={data.attention}
          className="home-surface--attention"
        />
        <aside className="home-side" aria-label="Decisions and active work">
          <Section
            title="Needs a decision"
            detail="Work paused for your input."
            items={data.decisions}
            className="home-surface--decision"
          />
          <Section
            title="In flight"
            detail="Work currently moving through Athena."
            items={data.in_flight}
            className="home-surface--running"
          />
        </aside>
      </div>
      <LatestPortfolioDocuments feed={data.latest_portfolio_documents} />
    </div>
  );
}
